import * as React from 'react';
import { Button, IconButton, Typography } from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import { makeStyles } from '@mui/styles';
import { FormattedMessage } from 'react-intl';
import { DropdownOption, InputType, Setting, CustomSetting } from 'types/types';

import { CustomFieldSection } from './CustomFieldSection';
import { ProjectStatusSection } from './ProjectStatusSection';
import { FilterFieldSection } from './FilterFieldSection';

const useStyles = makeStyles((theme) => ({
  rootContainer: { width: '45%' },
  helperText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: theme.spacing(1.4),
    lineHeight: theme.spacing(2.3),
    marginRight: theme.spacing(1.7)
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row'
  },
  buttonContainer: {
    width: '40%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  baselineButtonContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4)
  },
  removeButton: {
    marginTop: theme.spacing(1)
  }
}));

type MultipleInputFieldSectionProps = {
  inputSetting: CustomSetting;
  initialSelectValue: any;
  setSave: React.Dispatch<
    React.SetStateAction<Record<string, string | boolean>>
  >;
  changedFields: any;
  setChangedFields: React.Dispatch<React.SetStateAction<any>>;
  isFieldInvalid: any;
  setInvalidFields: React.Dispatch<React.SetStateAction<any>>;
  disabled?: boolean;
};

export const MultipleInputFieldSection: React.FC<MultipleInputFieldSectionProps> = ({
  inputSetting,
  initialSelectValue,
  setSave,
  changedFields,
  setChangedFields,
  isFieldInvalid,
  setInvalidFields,
  disabled = false
}: MultipleInputFieldSectionProps) => {
  const classes = useStyles();

  const settingId = inputSetting?.id as string;
  const options = inputSetting?.dropdownOptions as DropdownOption[];
  const [settings, setSettings] = React.useState<Setting[]>(
    Object.keys(initialSelectValue || {}).length === 0 ? [] : initialSelectValue
  );

  const handleRemoveButton = React.useCallback(
    (index: number) => {
      if (!settingId) return;

      setSettings((prev) => {
        if (prev.length === 1) return [];

        const updatedSettings = prev.filter((_, i) => i !== index);
        if (inputSetting?.type === InputType.RangeCriteriaSelection) {
          const updateRange = (start: string, end: string, status: string) => ({
            key: `${start}:${end}`,
            value: status
          });
          if (index === 0) {
            const nextRange = prev[index + 1];
            const nextRangeEnd = nextRange.key.split(':')[1];

            updatedSettings[0] = updateRange(
              '0',
              nextRangeEnd,
              nextRange.value || ''
            );
          } else {
            const prevRange = prev[index - 1];
            const prevRangeStart = prevRange.key.split(':')[0];
            const currRangeEnd = prev[index].key.split(':')[1];

            updatedSettings[index - 1] = updateRange(
              prevRangeStart,
              currRangeEnd,
              prevRange.value || ''
            );
          }
        }
        return updatedSettings;
      });

      setChangedFields((prev: any) => ({
        ...prev,
        [settingId]: prev[settingId].filter(
          (_: Setting, i: number) => i !== index
        )
      }));
      setInvalidFields((prev: any) => ({
        ...prev,
        [settingId]: prev[settingId].filter(
          (_: Setting, i: number) => i !== index
        )
      }));
    },
    [inputSetting?.type, setChangedFields, setInvalidFields, settingId]
  );

  const handleAddButton = React.useCallback(() => {
    if (!settingId) return;

    const getNextProjectStatus = (lastStatusIndex = 0) => {
      const len = options.length;
      const orderedOptions = [];
      for (let ind = 0; ind < len; ind++) {
        orderedOptions.push(options[(ind + lastStatusIndex) % len]);
      }

      const assignedOptions = new Set(settings.map((x: any) => x.value));
      const nextUnassignedOption = orderedOptions.find(
        (option: any) => !assignedOptions.has(Object.values(option)[0])
      ) as any;
      return Object.values(nextUnassignedOption)[0] as string;
    };

    const hasFilterOptions =
      inputSetting?.fieldOptions || inputSetting?.conditionOptions;

    setSettings((prev) => {
      if (inputSetting?.type === InputType.RangeCriteriaSelection) {
        const len = prev.length;
        if (len === 0) return [{ key: '0:100', value: getNextProjectStatus() }];

        let lastRange = prev[len - 1];
        let [lastRangeStart, lastRangeEnd] = lastRange.key
          .split(':')
          .map(Number);

        const newRangeStart = Math.ceil((lastRangeStart + lastRangeEnd) / 2);

        if (lastRangeStart === lastRangeEnd) {
          for (
            let ind = len - 1;
            0 <= ind && lastRangeStart === lastRangeEnd;
            ind--
          ) {
            lastRange = prev[ind];
            [lastRangeStart, lastRangeEnd] = lastRange.key
              .split(':')
              .map(Number);

            const newLastRangeStart =
              lastRangeStart === lastRangeEnd
                ? lastRangeStart - 1
                : lastRangeStart;

            lastRange.key = `${newLastRangeStart}:${lastRangeEnd - 1}`;
          }
        } else {
          lastRange.key = `${lastRangeStart}:${newRangeStart - 1}`;
        }

        const newRange = {
          key: `${newRangeStart}:100`,
          value: getNextProjectStatus(len - 1)
        };
        return [...prev, newRange];
      }
      if (hasFilterOptions) {
        return [
          ...prev,
          { key: '', value: null, field: null, condition: null }
        ];
      }
      return [...prev, { key: '', value: '' }];
    });

    const newFieldState = hasFilterOptions
      ? { field: false, condition: false, value: false }
      : { key: false, value: false };

    setChangedFields((prev: any) => ({
      ...prev,
      [settingId]: [...(prev[settingId] ? prev[settingId] : []), newFieldState]
    }));
    setInvalidFields((prev: any) => ({
      ...prev,
      [settingId]: [...(prev[settingId] ? prev[settingId] : []), newFieldState]
    }));
  }, [
    inputSetting?.type,
    inputSetting?.fieldOptions,
    inputSetting?.conditionOptions,
    options,
    setChangedFields,
    setInvalidFields,
    settingId,
    settings
  ]);

  React.useEffect(() => {
    if (!settingId) return;

    setSave((prev) => ({ ...prev, [settingId]: settings as any }));
  }, [setSave, settings, settingId]);

  const addButtonComponent = () => {
    const addButtonText = inputSetting?.addSettingName || '';
    const messageId = `dagCustomSettings.${addButtonText.split(' ').join('')}`;
    return (
      <Button
        color="primary"
        variant="contained"
        startIcon={<AddIcon />}
        disabled={
          disabled ||
          (inputSetting?.maxItems !== undefined &&
            settings.length >= inputSetting.maxItems) ||
          (inputSetting?.type === InputType.RangeCriteriaSelection &&
            settings.length >= (options.length || 6))
        }
        onClick={handleAddButton}
        data-testid="add-button"
      >
        <Typography>
          <FormattedMessage id={messageId} defaultMessage={addButtonText} />
        </Typography>
      </Button>
    );
  };

  const renderSettings = (setting: Setting, ind: number) => {
    const hasFilterOptions =
      inputSetting?.fieldOptions || inputSetting?.conditionOptions;

    return (
      <div className={classes.itemContainer} key={ind}>
        {inputSetting?.type === InputType.RangeCriteriaSelection ? (
          <ProjectStatusSection
            index={ind}
            settings={settings}
            setSettings={setSettings}
            inputSetting={inputSetting}
          />
        ) : hasFilterOptions ? (
          <FilterFieldSection
            index={ind}
            isInvalidField={isFieldInvalid[ind]}
            changedFields={changedFields}
            setChangedFields={setChangedFields}
            initialSelectValue={setting}
            setSettings={setSettings}
            inputSetting={inputSetting}
          />
        ) : (
          <CustomFieldSection
            index={ind}
            isInvalidField={isFieldInvalid[ind]}
            changedFields={changedFields}
            setChangedFields={setChangedFields}
            initialSelectValue={setting}
            setSettings={setSettings}
            inputSetting={inputSetting}
          />
        )}
        <div>
          {settings.length && (
            <IconButton
              className={classes.removeButton}
              color="secondary"
              onClick={() => handleRemoveButton(ind)}
              data-testid="remove-button"
              size="large"
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.rootContainer}>
      {inputSetting?.helperTextValue && (
        <div className={classes.helperText}>
          <span>{inputSetting.helperTextValue}</span>
        </div>
      )}
      {settings?.map((setting, ind) => renderSettings(setting, ind))}
      <div className={classes.baselineButtonContainer}>
        {addButtonComponent()}
      </div>
    </div>
  );
};
